import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, getMe, getRefund, type RefundRecord, type RefundState } from '../lib/api-client';
import { useI18n } from '../lib/i18n-context';
import { listRefundHistory, type RefundHistoryEntry } from '../lib/refund-history';

/** refund_state badge 顏色（同 RefundDetail 同步）。 */
function refundBadgeStyle(state: RefundState): React.CSSProperties {
  const terminal: RefundState[] = ['completed', 'cancelled', 'rejected'];
  const failed: RefundState[] = ['refund_failed', 'allowance_failed', 'manual_review'];
  const bg = terminal.includes(state) ? '#d1ecf1' : failed.includes(state) ? '#f8d7da' : '#fff3cd';
  const fg = terminal.includes(state) ? '#0c5460' : failed.includes(state) ? '#721c24' : '#856404';
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    background: bg,
    color: fg,
  };
}

/**
 * 我的退票 — `/me/refunds`。
 *
 * 來源：localStorage refund-history（RefundRequest 成功時 push）。
 * 對每筆呼叫 `GET /refunds/:id` 取目前 refund_state（後端為信任源；history 只記 id）。
 * 後端缺對應條目（404）→ 該列顯示「無法載入」但不從 history 移除（buyer 可能換裝置重看）。
 */
export function MyRefundsPage(): JSX.Element {
  const { t } = useI18n();
  const meQuery = useQuery({ queryKey: ['auth', 'me'], queryFn: getMe, retry: false });
  const history = useMemo(() => listRefundHistory(meQuery.data?.user_id), [meQuery.data?.user_id]);

  // 對每筆 history 拉現況；refetchInterval 不啟（list 頁不長 polling）
  const refundQueries = useQueries({
    queries: history.map((h) => ({
      queryKey: ['refund', h.refundId],
      queryFn: () => getRefund(h.refundId),
      retry: false,
      staleTime: 30_000,
    })),
  });

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('myRefunds.title')}</h1>
        <Link to="/" className="topbar-back">
          {t('common.backHome')}
        </Link>
      </header>

      {history.length === 0 ? (
        <p style={{ color: '#777' }}>{t('myRefunds.empty')}</p>
      ) : (
        <article className="card" style={{ padding: 0 }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {history.map((h, idx) => (
              <RefundRow
                key={h.refundId}
                entry={h}
                query={refundQueries[idx]}
                last={idx === history.length - 1}
              />
            ))}
          </ul>
        </article>
      )}
    </div>
  );
}

interface RefundRowProps {
  entry: RefundHistoryEntry;
  query: { isPending: boolean; isError: boolean; error: unknown; data?: RefundRecord } | undefined;
  last: boolean;
}

function RefundRow({ entry, query, last }: RefundRowProps): JSX.Element {
  const { t } = useI18n();
  const r = query?.data;
  return (
    <li
      style={{
        padding: '10px 14px',
        borderBottom: last ? 'none' : '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Link to={`/refunds/${entry.refundId}`} style={{ fontFamily: 'monospace', fontSize: 14 }}>
          {entry.refundId.slice(0, 8)}…
        </Link>
        {entry.ticketId !== undefined && (
          <span style={{ marginLeft: 8, fontSize: 12, color: '#777' }}>
            {t('myRefunds.ticketRef', { id: `${entry.ticketId.slice(0, 8)}…` })}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {query?.isPending && <span style={{ fontSize: 12, color: '#999' }}>…</span>}
        {query?.isError && (
          <span
            style={{ fontSize: 12, color: '#999' }}
            title={query.error instanceof ApiError ? query.error.message : t('common.unknownError')}
          >
            {t('myRefunds.rowLoadFailed')}
          </span>
        )}
        {r !== undefined && (
          <>
            <span style={refundBadgeStyle(r.refund_state)}>{r.refund_state}</span>
            <span style={{ fontSize: 12, color: '#999' }}>{r.refund_amount}</span>
          </>
        )}
        <span style={{ fontSize: 12, color: '#bbb' }}>
          {new Date(entry.addedAt).toLocaleDateString()}
        </span>
      </div>
    </li>
  );
}
